import React, { useEffect, useState, useRef } from 'react';
import { View, ScrollView, StyleSheet, RefreshControl } from 'react-native';
import {
  Card,
  Text,
  Button,
  ProgressBar,
  Chip,
  Surface,
  useTheme,
  ActivityIndicator,
} from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation, useRoute } from '@react-navigation/native';
import {
  Calendar,
  Clock,
  Target,
  TrendingUp,
  Award,
} from 'lucide-react-native';
import { useAppDispatch, useAppSelector } from '../../store';
import { fetchMyJourneys } from '../../store/slices/journeySlice';
import type { Journey, JourneyStatus } from '@coffeeplexity/shared';
import { logger } from '../../utils/logger';

// const { width } = Dimensions.get('window');

export function MyJourneyScreen() {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();
  const route = useRoute();
  const dispatch = useAppDispatch();

  const { journeys, loading } = useAppSelector(state => state.journey);
  const [refreshing, setRefreshing] = useState(false);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // Check if we should skip refresh (e.g., coming from create journey)
    const params = (route.params as any) || {};

    // Skip if we've already fetched or if skipRefresh is true and we have journeys
    if (hasFetchedRef.current || (params.skipRefresh && journeys.length > 0)) {
      logger.log('Skipping fetch - already have data');
      return;
    }

    logger.log('Fetching journeys...');
    dispatch(fetchMyJourneys('current_user')); // TODO: Get from auth context
    hasFetchedRef.current = true;
  }, [dispatch, route.params, journeys.length]);

  const onRefresh = async () => {
    setRefreshing(true);
    await dispatch(fetchMyJourneys('current_user')); // TODO: Get from auth context
    setRefreshing(false);
  };

  const handleJourneyPress = (journey: Journey) => {
    (navigation as any).navigate('journeyDetail', { journeyId: journey.id });
  };

  const handleWeeklyGoalsPress = (journey: Journey) => {
    (navigation as any).navigate('weeklyGoals', {
      journeyId: journey.id,
      weekNumber: journey.current_week,
    });
  };

  const handleProgressTrackerPress = (journey: Journey) => {
    (navigation as any).navigate('progressTracker', { journeyId: journey.id });
  };

  const activeJourneys = journeys.filter(j => j.status === 'active');
  const completedJourneys = journeys.filter(j => j.status === 'completed');
  const pausedJourneys = journeys.filter(j => j.status === 'paused');

  // Debug logging
  logger.log('MyJourneyScreen render:', {
    totalJourneys: journeys.length,
    activeJourneys: activeJourneys.length,
    completedJourneys: completedJourneys.length,
    pausedJourneys: pausedJourneys.length,
    loading: loading.journeys,
  });

  if (loading.journeys && journeys.length === 0) {
    return (
      <View
        style={[
          styles.container,
          styles.centered,
          { backgroundColor: theme.colors.background },
        ]}
      >
        <ActivityIndicator size='large' />
        <Text style={styles.loadingText}>Loading your journeys...</Text>
      </View>
    );
  }

  return (
    <ScrollView
      style={[
        styles.container,
        { paddingTop: insets.top, backgroundColor: theme.colors.background },
      ]}
      contentContainerStyle={styles.contentContainer}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
      }
    >
      {/* Header */}
      <View style={styles.header}>
        <Text variant='headlineMedium' style={styles.title}>
          My Coffee Journey
        </Text>
        <Text variant='bodyMedium' style={styles.subtitle}>
          Track your progress and continue learning
        </Text>
      </View>

      {/* Quick Stats */}
      {activeJourneys.length > 0 && (
        <Surface style={styles.statsCard} elevation={1}>
          <View style={styles.statsRow}>
            <View style={styles.statItem}>
              <Target size={20} color={theme.colors.primary} />
              <Text variant='labelSmall' style={styles.statLabel}>
                Active
              </Text>
              <Text variant='titleMedium' style={styles.statValue}>
                {activeJourneys.length}
              </Text>
            </View>
            <View style={styles.statItem}>
              <Award size={20} color={theme.colors.tertiary} />
              <Text variant='labelSmall' style={styles.statLabel}>
                Completed
              </Text>
              <Text variant='titleMedium' style={styles.statValue}>
                {completedJourneys.length}
              </Text>
            </View>
            <View style={styles.statItem}>
              <TrendingUp size={20} color={theme.colors.secondary} />
              <Text variant='labelSmall' style={styles.statLabel}>
                Total
              </Text>
              <Text variant='titleMedium' style={styles.statValue}>
                {journeys.length}
              </Text>
            </View>
          </View>
        </Surface>
      )}

      {/* Active Journeys */}
      {activeJourneys.length > 0 && (
        <View style={styles.section}>
          <Text variant='titleLarge' style={styles.sectionTitle}>
            Active Journeys
          </Text>
          {activeJourneys.map(journey => (
            <JourneyCard
              key={journey.id}
              journey={journey}
              onPress={() => handleJourneyPress(journey)}
              onWeeklyGoalsPress={() => handleWeeklyGoalsPress(journey)}
              onProgressPress={() => handleProgressTrackerPress(journey)}
              theme={theme}
            />
          ))}
        </View>
      )}

      {/* Paused Journeys */}
      {pausedJourneys.length > 0 && (
        <View style={styles.section}>
          <Text variant='titleLarge' style={styles.sectionTitle}>
            Paused Journeys
          </Text>
          {pausedJourneys.map(journey => (
            <JourneyCard
              key={journey.id}
              journey={journey}
              onPress={() => handleJourneyPress(journey)}
              onWeeklyGoalsPress={() => handleWeeklyGoalsPress(journey)}
              onProgressPress={() => handleProgressTrackerPress(journey)}
              theme={theme}
            />
          ))}
        </View>
      )}

      {/* Completed Journeys */}
      {completedJourneys.length > 0 && (
        <View style={styles.section}>
          <Text variant='titleLarge' style={styles.sectionTitle}>
            Completed Journeys
          </Text>
          {completedJourneys.map(journey => (
            <JourneyCard
              key={journey.id}
              journey={journey}
              onPress={() => handleJourneyPress(journey)}
              onWeeklyGoalsPress={() => handleWeeklyGoalsPress(journey)}
              onProgressPress={() => handleProgressTrackerPress(journey)}
              theme={theme}
            />
          ))}
        </View>
      )}

      {/* Empty State */}
      {journeys.length === 0 && !loading.journeys && (
        <View style={styles.emptyState}>
          <Calendar size={64} color={theme.colors.outline} />
          <Text variant='headlineSmall' style={styles.emptyTitle}>
            No Journeys Yet
          </Text>
          <Text variant='bodyMedium' style={styles.emptySubtitle}>
            Start your coffee learning journey today!
          </Text>
          <Button
            mode='contained'
            onPress={() => (navigation as any).navigate('createJourney')}
            style={styles.createButton}
          >
            Create Your First Journey
          </Button>
        </View>
      )}

      {/* Create Journey Button */}
      {journeys.length > 0 && (
        <Button
          mode='outlined'
          onPress={() => (navigation as any).navigate('createJourney')}
          style={styles.createButton}
          icon='plus'
        >
          Start New Journey
        </Button>
      )}
    </ScrollView>
  );
}

interface JourneyCardProps {
  journey: Journey;
  onPress: () => void;
  onWeeklyGoalsPress: () => void;
  onProgressPress: () => void;
  theme: any;
}

function JourneyCard({
  journey,
  onPress,
  onWeeklyGoalsPress,
  onProgressPress,
  theme,
}: JourneyCardProps) {
  const getStatusColor = (status: JourneyStatus) => {
    switch (status) {
      case 'active':
        return theme.colors.primary;
      case 'paused':
        return theme.colors.outline;
      case 'completed':
        return theme.colors.tertiary;
      case 'abandoned':
        return theme.colors.error;
      default:
        return theme.colors.outline;
    }
  };

  const getStatusText = (status: JourneyStatus) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'paused':
        return 'Paused';
      case 'completed':
        return 'Completed';
      case 'abandoned':
        return 'Abandoned';
      default:
        return status;
    }
  };

  return (
    <Card style={styles.journeyCard} onPress={onPress}>
      <Card.Content>
        <View style={styles.journeyHeader}>
          <View style={styles.journeyTitleRow}>
            <Text variant='titleMedium' style={styles.journeyTitle}>
              {journey.title}
            </Text>
            <Chip
              mode='outlined'
              textStyle={{ color: getStatusColor(journey.status) }}
              style={{ borderColor: getStatusColor(journey.status) }}
            >
              {getStatusText(journey.status)}
            </Chip>
          </View>
          {journey.description && (
            <Text variant='bodySmall' style={styles.journeyDescription}>
              {journey.description}
            </Text>
          )}
        </View>

        <View style={styles.progressSection}>
          <View style={styles.progressHeader}>
            <Text variant='labelMedium'>Progress</Text>
            <Text variant='labelMedium'>
              {Math.round(journey.progress_percentage)}%
            </Text>
          </View>
          <ProgressBar
            progress={journey.progress_percentage / 100}
            color={theme.colors.primary}
            style={styles.progressBar}
          />
        </View>

        <View style={styles.journeyMeta}>
          <View style={styles.metaItem}>
            <Calendar size={16} color={theme.colors.onSurfaceVariant} />
            <Text variant='bodySmall' style={styles.metaText}>
              Week {journey.current_week} of {journey.duration_weeks}
            </Text>
          </View>
          <View style={styles.metaItem}>
            <Clock size={16} color={theme.colors.onSurfaceVariant} />
            <Text variant='bodySmall' style={styles.metaText}>
              {journey.time_commitment_hours_per_week}h/week
            </Text>
          </View>
        </View>

        <View style={styles.focusAreas}>
          {[...journey.focus_areas].slice(0, 3).map((area, index) => (
            <Chip key={index} mode='outlined' compact style={styles.focusChip}>
              {area}
            </Chip>
          ))}
          {journey.focus_areas.length > 3 && (
            <Chip mode='outlined' compact style={styles.focusChip}>
              +{journey.focus_areas.length - 3} more
            </Chip>
          )}
        </View>

        {journey.status === 'active' && (
          <View style={styles.actionButtons}>
            <Button
              mode='contained'
              onPress={onWeeklyGoalsPress}
              style={styles.actionButton}
              compact
            >
              This Week
            </Button>
            <Button
              mode='outlined'
              onPress={onProgressPress}
              style={styles.actionButton}
              compact
            >
              Progress
            </Button>
          </View>
        )}
      </Card.Content>
    </Card>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 32,
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
  },
  header: {
    marginBottom: 24,
  },
  title: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  subtitle: {
    opacity: 0.7,
  },
  statsCard: {
    padding: 16,
    marginBottom: 24,
    borderRadius: 12,
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statLabel: {
    marginTop: 4,
    opacity: 0.7,
  },
  statValue: {
    marginTop: 2,
    fontWeight: 'bold',
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    marginBottom: 16,
    fontWeight: 'bold',
  },
  journeyCard: {
    marginBottom: 16,
    borderRadius: 12,
  },
  journeyHeader: {
    marginBottom: 16,
  },
  journeyTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  journeyTitle: {
    flex: 1,
    marginRight: 12,
    fontWeight: 'bold',
  },
  journeyDescription: {
    opacity: 0.7,
    lineHeight: 20,
  },
  progressSection: {
    marginBottom: 16,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
  },
  journeyMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  metaText: {
    marginLeft: 6,
    opacity: 0.7,
  },
  focusAreas: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  focusChip: {
    marginRight: 8,
    marginBottom: 4,
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  actionButton: {
    flex: 1,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 48,
  },
  emptyTitle: {
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptySubtitle: {
    textAlign: 'center',
    opacity: 0.7,
    marginBottom: 24,
  },
  createButton: {
    marginTop: 16,
  },
});
